<main id="main-content" class="h-screen flex flex-col pl-8 py-4 pr-4 transition-all duration-0">
    <div class="w-full flex flex-col p-2 items-center gap-2">
        <div class="w-full flex flex-row items-center justify-between">
            <div class="w-full flex gap-2 items-center justify-start">
                <h1 class="oswald-font text-3xl uppercase">
                    Prepare your EOP
                </h1>
                <div data-popover-target="popover-right" data-popover-placement="right" type="button">
                    <lucide-icon [name]="icons.CircleHelp" [size]=24 class="text-red-500"></lucide-icon>
                </div>
                <div data-popover id="popover-right" role="tooltip" class="absolute z-10 invisible inline-block w-[600px] text-sm text-gray-500 transition-opacity duration-300 bg-white border border-gray-200 rounded-md shadow-xs opacity-0">
                    <div class="px-3 py-2">
                        <p>
                            To add templated sections from the REMs Basic Plan, click on 
                            <span class="inline-flex px-3.5 h-8 rounded-md border-2 border-nside-gray-200 text-nside-gray-200 oswald-font flex items-center justify-center gap-2">
                                <lucide-icon [img]="icons.Pencil"></lucide-icon>
                                Edit Plan
                            </span>. A pop-up will appear with all recommended templates for your institution's Basic Plan. Check the box beside each that you would like to include in your <strong>Emergency Operations Plan</strong>. It is recommended that all sections be included.
                        </p>
                        <p>Once completed, click 
                            <span class="inline-flex px-3.5 h-8 flex items-center justify-center text-white oswald-font bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 rounded-md">
                                Save
                            </span>.
                        </p>
                        <p>
                            Sections can be added or removed at any time. Removing a section will not delete any information that was saved for that section prior to its removal. However, it will not be printed in your school's <strong>Emergency Operations Plan</strong> if removed.
                        </p>
                        <p>
                            There may be times in which you would like to include specific sections in your <strong>Emergency Operations Plan</strong> that do not fit into other sections of the Basic Plan. These may include requirements for local, state, or regional laws as well preferences by your institution. 
                        </p>
                        <p>
                            To create a customized section, click on 
                            <span class="inline-flex px-3.5 h-8 rounded-md border-2 border-nside-gray-200 text-nside-gray-200 oswald-font flex items-center justify-center gap-2">
                                <lucide-icon [img]="icons.Plus"></lucide-icon>
                                Add New Page
                            </span>. In the pop-up, enter the title of the section. Each section's title must be distinct. 
                        </p>
                        <p>
                            Once the page has been created, it will appear under the Custom Pages section. To edit the custom page's information, click on the page name. 
                        </p>
                    </div>
                    <div data-popper-arrow></div>
                </div>
            </div>
        </div>
    </div>
    
    <!-- MAIN CONTENT -->
    <div class="p-2 w-full flex flex-grow flex-row items-start gap-8 overflow-y-auto">
        <div class="w-1/2 max-h-full bg-white rounded-md shadow-custom-2 p-6 flex flex-col items-center justify-start gap-4">
            <div class="w-full flex items-center justify-between gap-2">
                <h2 class="oswald-font text-3xl text-nside-orange-400">
                    The Basic Plan
                </h2>
                <button
                    *ngIf="canSave()"
                    class="w-22 px-2.5 py-1 rounded-md border-2 border-nside-gray-200 text-nside-gray-200 hover:text-white hover:bg-nside-gray-200 oswald-font flex items-center justify-center gap-2"
                    (click)="$event.preventDefault(); handleOpenModal('edit-plan');"
                >
                    <lucide-icon [img]="icons.Pencil"></lucide-icon>
                    Edit Plan
                </button>
            </div>
            <ng-container *ngIf="buildingEopSections.length > 0; else emptyPlan">
                <div class="w-full h-full flex flex-col items-start gap-1 overflow-y-auto">
                    <a
                        *ngFor="let page of buildingEopSections; let i = index"
                        routerLink="/basic-plan/{{ page.route }}"
                        class="py-1 font-semibold border-b border-gray-200 hover:text-nside-orange-400"
                    >
                        {{ i + 1 }}. {{ page.type }}
                    </a>
                </div>
            </ng-container>
            <ng-template #emptyPlan>
                <div class="w-full">
                    <p class="w-full text-gray-500 font-medium">
                        Empty pages.
                    </p>
                    <p *ngIf="canSave()" class="w-full text-gray-500 font-medium">
                        Click the button above to update the plan.
                    </p>
                </div>
            </ng-template>
        </div>
        <div class="w-1/2 max-h-full bg-white rounded-md shadow-custom-2 p-6 flex flex-col items-center justify-start gap-4">
            <div class="w-full flex items-center justify-between gap-2">
                <h2 class="oswald-font text-3xl">
                    Custom Pages
                </h2>
                <button
                    *ngIf="canSave()"
                    class="w-22 px-2.5 py-1 rounded-md border-2 border-nside-gray-200 text-nside-gray-200 hover:text-white hover:bg-nside-gray-200 oswald-font flex items-center justify-center gap-2"
                    (click)="$event.preventDefault(); handleOpenModal('add-custom-page');"
                >
                    <lucide-icon [img]="icons.Plus"></lucide-icon>
                    Add New Page
                </button>
            </div>
            <ng-container *ngIf="buildingCustomEopSections.length > 0; else emptyCustomPlan">
                <div class="w-full h-full flex flex-col items-start gap-1 overflow-y-auto">
                    <div class="w-full flex gap-6 items-center justify-between" *ngFor="let page of buildingCustomEopSections; let i = index">
                        <a
                            routerLink="/custom-page/{{ page.id }}"
                            class="min-w-0 flex-1 py-1 font-semibold border-b border-gray-200 hover:text-nside-orange-400 whitespace-pre-line break-words [overflow-wrap:anywhere]"
                        >
                            {{ i + 1 }}. {{ page.title }}
                        </a>
                        <div *ngIf="canSave()" class="flex items-center justify-center gap-1">
                            <button 
                                [attr.data-tooltip-target]="'tooltip-' + page.id + '-edit'"  data-tooltip-placement="bottom"
                                class="w-8 h-8 rounded border-2 border-nside-gray-200 text-nside-gray-200 flex items-center justify-center hover:text-white hover:bg-nside-gray-200"
                                (click)="$event.preventDefault(); handleOpenModal('edit-custom-page', page);"
                            >
                                <lucide-icon [img]="icons.Pencil" size="20"></lucide-icon>
                            </button>
                            <div [id]="'tooltip-' + page.id + '-edit'" role="tooltip" class="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg shadow-sm opacity-0 tooltip">
                                Edit Page
                                <div class="tooltip-arrow" data-popper-arrow></div>
                            </div>
                            <button 
                                [attr.data-tooltip-target]="'tooltip-' + page.id + '-delete'" data-tooltip-placement="bottom"
                                class="w-8 h-8 flex items-center justify-center font-normal text-white oswald-font bg-gradient-to-b from-nside-gray-300 to-nside-gray-400 rounded hover:bg-gradient-to-b hover:from-red-600 hover:to-red-700 cursor-pointer"
                                (click)="$event.preventDefault(); handleOpenModal('delete-confirmation', page);"
                            >
                                <lucide-icon [img]="icons.Trash" size="20"></lucide-icon>
                            </button>
                            <div [id]="'tooltip-' + page.id + '-delete'" role="tooltip" class="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg shadow-sm opacity-0 tooltip">
                                Delete Page
                                <div class="tooltip-arrow" data-popper-arrow></div>
                            </div>
                        </div>
                    </div>
                </div>
            </ng-container>
            <ng-template #emptyCustomPlan>
                <div class="w-full">
                    <p class="w-full text-gray-500 font-medium">
                        Empty custom pages.
                    </p>
                    <p *ngIf="canSave()" class="w-full text-gray-500 font-medium">
                        Click the button above to create a new custom page.
                    </p>
                </div>
            </ng-template>
        </div>
    </div>

    <edit-plan
        *ngIf="showEditPlan"
        [currentPlan]="buildingEopSections"
        (onClose)="handleCloseModal('edit-plan')"
        (onSave)="handleSave($event, 'edit-plan')"
    ></edit-plan>

    <add-custom-page
        *ngIf="showAddCustomPage"
        (onClose)="handleCloseModal('add-custom-page')"
        (onSave)="handleSave($event, 'add-custom-page')"
    ></add-custom-page>

    <edit-custom-page
        *ngIf="showEditCustomPage"
        [customPage]="selectedCustomPage"
        (onClose)="handleCloseModal('edit-custom-page')"
        (onSave)="handleSave($event, 'edit-custom-page')"
    ></edit-custom-page>

   <delete-confirmation
        *ngIf="showDeleteConfirmation"
        [text]="'this page'"
        [itemToDelete]="selectedCustomPage"
        (onClose)="handleCloseModal('delete-confirmation')"
        (onDelete)="deleteCustomPage($event)"
    ></delete-confirmation>

    <popup-message 
        [type]="messageModal"
        (close)="resetMessageModal()"
        [message]="popupMessage"
    ></popup-message>

    <introduction></introduction>

    <div *ngIf="loading">
        <spinner></spinner>
    </div>

    <prv-next-buttons [totalPages]="(eopNavigationService.totalPages$ | async) || []" />
</main>